import { formatRupiah } from "@/lib/display-format";
import { useEffect, useMemo, useState } from "react";
import { Check, LoaderCircle, Target, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { businessUnitName, type BusinessUnit } from "@/lib/business-units";
import { budgetTone, calculateBudgetOverview, normalizeBudgetMonth, type FinanceBudget } from "@/lib/budgeting";
import { categoryName, type FinanceCategory } from "@/lib/categories";
import { type Language, type Transaction } from "@/lib/finance";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  budgets: FinanceBudget[];
  transactions: Transaction[];
  businessUnits: BusinessUnit[];
  categories: FinanceCategory[];
  defaultMonth: string;
  defaultBusinessUnit: string | null;
  language: Language;
  isOwner: boolean;
  onSave: (budget: FinanceBudget) => Promise<void>;
  onDelete: (budget: Pick<FinanceBudget, "month" | "businessUnit" | "category">) => Promise<void>;
};

function rupiah(value: number, language: Language) {
  return formatRupiah(value, language);
}

export function BudgetSettings({ open, onOpenChange, budgets, transactions, businessUnits, categories, defaultMonth, defaultBusinessUnit, language, isOwner, onSave, onDelete }: Props) {
  const [month, setMonth] = useState(defaultMonth.slice(0, 7));
  const [unit, setUnit] = useState(defaultBusinessUnit || "all");
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [busyKey, setBusyKey] = useState("");
  const monthStart = normalizeBudgetMonth(month);
  const selectedUnit = unit === "all" ? null : unit;
  const costCategories = useMemo(() => categories.filter((category) => category.active && !category.system && !category.parentSlug && category.kind === "cost").map((category) => category.slug), [categories]);
  const overview = useMemo(() => open ? calculateBudgetOverview(budgets, transactions, monthStart, selectedUnit) : null, [open, budgets, transactions, monthStart, selectedUnit]);
  const existing = useMemo(() => new Map(budgets.filter((item) => normalizeBudgetMonth(item.month) === monthStart && (item.businessUnit || null) === selectedUnit).map((item) => [item.category, item])), [budgets, monthStart, selectedUnit]);
  const text = language === "id" ? {
    title: "Budgeting",
    description: "Rencana dan realisasi pengeluaran per bulan, brand, dan kategori.",
    allBrands: "Semua brand",
    plan: "Rencana",
    actual: "Realisasi",
    remaining: "Sisa",
    used: "terpakai",
    noBudget: "Belum ada budget",
    save: "Simpan",
    delete: "Hapus budget",
    saved: "Budget berhasil disimpan.",
    deleted: "Budget berhasil dihapus.",
    failed: "Budget gagal diperbarui. Pastikan migrasi v0.12.0 sudah dijalankan.",
    ownerOnly: "Hanya Owner yang dapat mengubah budget.",
    needCategory: "Belum ada kategori pengeluaran. Tambahkan kategori sesuai kebutuhan nyata melalui menu Kategori & pembelajaran.",
  } : {
    title: "Budgeting",
    description: "Monthly expense plans and actuals by brand and category.",
    allBrands: "All brands",
    plan: "Plan",
    actual: "Actual",
    remaining: "Remaining",
    used: "used",
    noBudget: "No budget yet",
    save: "Save",
    delete: "Delete budget",
    saved: "Budget saved.",
    deleted: "Budget deleted.",
    failed: "Budget could not be updated. Make sure migration v0.12.0 has run.",
    ownerOnly: "Only an Owner can change budgets.",
    needCategory: "There are no expense categories yet. Add categories based on actual needs in Categories & learning.",
  };

  useEffect(() => {
    if (!open) return;
    setMonth(defaultMonth.slice(0, 7));
    setUnit(defaultBusinessUnit || "all");
  }, [open, defaultMonth, defaultBusinessUnit]);

  useEffect(() => {
    const next: Record<string, string> = {};
    for (const category of costCategories) next[category] = existing.get(category)?.amount ? String(existing.get(category)?.amount) : "";
    setDrafts(next);
  }, [monthStart, selectedUnit, budgets, costCategories]);

  async function save(category: string) {
    const amount = Number(drafts[category] || 0);
    if (!Number.isFinite(amount) || amount < 0) return;
    setBusyKey(category);
    try {
      await onSave({ month: monthStart, businessUnit: selectedUnit, category, amount });
      toast.success(text.saved);
    } catch {
      toast.error(text.failed);
    } finally {
      setBusyKey("");
    }
  }

  async function remove(category: string) {
    setBusyKey(category);
    try {
      await onDelete({ month: monthStart, businessUnit: selectedUnit, category });
      toast.success(text.deleted);
    } catch {
      toast.error(text.failed);
    } finally {
      setBusyKey("");
    }
  }

  if (!overview) return null;
  const tone = budgetTone(overview.usagePercent);
  const progressClass = tone === "over" ? "bg-red-500" : tone === "warning" ? "bg-amber-500" : "bg-emerald-500";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-2xl">
        <SheetHeader className="border-b px-4 py-5 sm:px-6"><SheetTitle className="flex items-center gap-2"><Target className="size-5" />{text.title}</SheetTitle><SheetDescription>{text.description}</SheetDescription></SheetHeader>
        <div className="space-y-5 p-4 sm:p-6">
          <div className="grid gap-3 sm:grid-cols-2"><label><span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{language === "id" ? "Bulan" : "Month"}</span><Input type="month" value={month} onChange={(event) => setMonth(event.target.value)} /></label><label><span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Brand</span><Select value={unit} onValueChange={setUnit}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">{text.allBrands}</SelectItem>{businessUnits.filter((item) => item.active && item.slug !== "unassigned").map((item) => <SelectItem key={item.slug} value={item.slug}>{businessUnitName(item.slug, language, businessUnits)}</SelectItem>)}</SelectContent></Select></label></div>

          <section className="overflow-hidden rounded border border-[#ff8c3a]">
            <div className="bg-[#ff8c3a] px-4 py-2 text-center text-xs font-bold uppercase tracking-wide text-white">{language === "id" ? "Rencana vs realisasi" : "Plan vs actual"}</div>
            <div className="grid grid-cols-3 gap-1 p-4"><div className="bg-[#fde3db] p-3 text-[#c9583f]"><p className="text-[10px] font-bold uppercase tracking-wide">{text.plan}</p><p className="mt-1 text-sm font-bold tabular-nums">{rupiah(overview.budget, language)}</p></div><div className="bg-[#fff4c8] p-3 text-[#8e7412]"><p className="text-[10px] font-bold uppercase tracking-wide">{text.actual}</p><p className="mt-1 text-sm font-bold tabular-nums">{rupiah(overview.actual, language)}</p></div><div className="bg-[#d7f3f1] p-3 text-[#18858b]"><p className="text-[10px] font-bold uppercase tracking-wide">{text.remaining}</p><p className={`mt-1 text-sm font-bold tabular-nums ${overview.remaining < 0 ? "text-red-700 dark:text-red-300" : ""}`}>{rupiah(overview.remaining, language)}</p></div></div>
            <div className="px-4 pb-4"><div className="h-4 overflow-hidden border border-[#9b3b6c] bg-[#f1e0e9]"><div className={`h-full transition-all ${progressClass}`} style={{ width: `${Math.min(100, Math.max(0, overview.usagePercent || 0))}%` }} /></div><p className="mt-2 text-right text-xs text-muted-foreground">{overview.usagePercent === null ? text.noBudget : `${overview.usagePercent.toFixed(1)}% ${text.used}`}</p></div>
          </section>

          {!isOwner && <p className="rounded-xl border bg-muted/25 p-4 text-sm text-muted-foreground">{text.ownerOnly}</p>}
          {!costCategories.length && <p className="rounded border border-amber-500/30 bg-amber-500/8 p-4 text-sm text-muted-foreground">{text.needCategory}</p>}
          <div className="space-y-3">
            {costCategories.map((category) => {
              const usage = overview.items.find((item) => item.category === category);
              const hasBudget = existing.has(category);
              return <article key={category} className="rounded border-l-4 border-l-[#ff8c3a] p-4"><div className="flex flex-wrap items-start justify-between gap-2"><div><p className="font-semibold">{categoryName(category, language, categories)}</p><p className="mt-1 text-xs text-muted-foreground">{text.actual}: {rupiah(usage?.actual || 0, language)}</p></div>{usage?.usagePercent !== null && usage?.usagePercent !== undefined && <Badge variant="outline" className={usage.usagePercent > 100 ? "border-red-500/30 text-red-600" : usage.usagePercent >= 85 ? "border-amber-500/30 text-amber-600" : "border-emerald-500/30 text-emerald-600"}>{usage.usagePercent.toFixed(0)}%</Badge>}</div>{isOwner && <div className="mt-3 grid grid-cols-[1fr_auto_auto] gap-2"><Input type="number" min="0" step="1000" inputMode="numeric" value={drafts[category] || ""} onChange={(event) => setDrafts((current) => ({ ...current, [category]: event.target.value }))} placeholder="0" aria-label={`${text.plan} ${categoryName(category, language, categories)}`} /><Button size="icon-lg" disabled={busyKey === category || !monthStart} onClick={() => void save(category)} aria-label={text.save}>{busyKey === category ? <LoaderCircle className="animate-spin" /> : <Check />}</Button><Button size="icon-lg" variant="ghost" className="text-destructive" disabled={!hasBudget || busyKey === category} onClick={() => void remove(category)} aria-label={text.delete}><Trash2 /></Button></div>}</article>;
            })}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
